import { useEffect, useState, type JSX } from 'react';
import { useNavigate } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Badge,
  Button,
  Card,
  ChevronLeftIcon,
  ChevronRightIcon,
  Field,
  Input,
  Radio,
  RadioGroup,
  Textarea
} from '@viniciomendesr/rayrovisk-design-system';
import { ProfileAvatar } from '../components/ProfileAvatar';
import { ChipChoice, ChipMulti, options, type ChipOption } from '../components/chips';
import { VoiceRulesStep } from './VoiceRulesStep';
import { instagramHandle, isInstagramHandle } from '../api/handle';
import { useConfirmInstagram, useProfile, useSaveProfile } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import type { CreatorProfile, ProfileDraft } from '../api/types';

/** Four screens she fills, plus the summary. It was six until 2026-08-06. */
const STEPS = 5;

/**
 * What she publishes, not where. The list was `Reels, Stories, Carrossel,
 * TikTok, YouTube Shorts` — five destinations — and became five shapes on
 * 2026-08-06, because the prompt's format block cares how long the thing is
 * and whether it is spoken, which is what a shape says and a destination does
 * not: a Reel and a TikTok produce the same script.
 *
 * The strings are stored verbatim in `format.platforms`, so an existing profile
 * keeps whatever it was saved with and simply shows none of these checked.
 */
const FORMATS = options(
  'Stories',
  'Carrossel',
  'Vídeo longo',
  'Reels (Vídeo curto)',
  'Post estático'
);

/**
 * Says where the photo comes from, because she can no longer put one there.
 * "Conferir" below is the only thing that fills it, so the hint has to name
 * that button rather than describe the picture — otherwise an empty avatar
 * reads as a form she forgot to fill.
 */
const PHOTO_HINT = 'Vem do seu Instagram quando você confere o perfil aqui embaixo.';

const LENGTHS: ChipOption[] = [
  { value: 'short', label: '15 segundos' },
  { value: 'medium', label: '30 segundos' },
  { value: 'long', label: '60 segundos' }
];

const PRICE_RANGES = options(
  'Até R$ 100',
  'R$ 100 a R$ 400',
  'R$ 400 a R$ 1.500',
  'Acima de R$ 1.500'
);

/**
 * The niche reaches `buildPrompt` as the words she picked, so these are broad
 * enough to cover most of who buys and specific enough to be worth reading in
 * a prompt. Anything else she writes, and hers is stored the same way.
 */
const NICHES = options(
  'Confeitaria',
  'Gastronomia',
  'Moda',
  'Beleza',
  'Estética',
  'Barbearia',
  'Fitness',
  'Nutrição',
  'Saúde',
  'Psicologia',
  'Maternidade',
  'Pets',
  'Decoração',
  'Artesanato',
  'Casamentos e festas',
  'Fotografia',
  'Viagem',
  'Imóveis',
  'Finanças',
  'Advocacia',
  'Educação',
  'Marketing digital'
);

const OBJECTIONS = options(
  'acham caro',
  'acham que não é para elas',
  'dizem que vão pensar e somem',
  'comparam com o mais barato',
  'não entendem o prazo'
);

interface Draft {
  niche: string;
  audience: string;
  instagram: string;
  instagramConfirmedAt: string;
  instagramThumbnailKey: string;
  followerCount: string;
  postCount: string;
  countsConfirmedAt: string;
  sells: string;
  priceRange: string;
  objection: string;
  /**
   * Kept on the draft with nothing writing to it since 2026-08-06, when the
   * screen that collected captions was dropped. An existing profile still round
   * trips its samples through here instead of losing them on the next edit,
   * which is the whole reason this field survives the redesign.
   */
  samples: string[];
  wordsUsed: string[];
  wordsAvoided: string[];
  appearsOnCamera: string;
  preferredLength: string;
  platforms: string[];
  forbiddenTopics: string[];
  forbiddenClaims: string[];
  regulated: boolean;
}

const EMPTY: Draft = {
  niche: '',
  audience: '',
  instagram: '',
  instagramConfirmedAt: '',
  instagramThumbnailKey: '',
  followerCount: '',
  postCount: '',
  countsConfirmedAt: '',
  sells: '',
  priceRange: '',
  objection: '',
  samples: [],
  wordsUsed: [],
  wordsAvoided: [],
  appearsOnCamera: 'as-vezes',
  preferredLength: 'medium',
  platforms: ['Stories', 'Carrossel', 'Reels (Vídeo curto)'],
  forbiddenTopics: [],
  forbiddenClaims: [],
  regulated: false
};

/**
 * Screen 07. Four steps and a summary, one save: the profile is written once,
 * on the last step she fills, with everything collected. Saving per step would
 * leave half a profile on the server for anyone who closed the tab in the
 * middle, and half a profile is what renders a prompt with a blank in it.
 *
 * **It was six steps until 2026-08-06.** The one that went was `Cole de três a
 * cinco legendas que você escreveu` — the voice block, which the schema calls
 * the thing that carries the quality. In its place step 1 asks for an Instagram
 * handle and says it is what lets the tool see her tone.
 *
 * Nothing reads that handle, so every profile filled here reaches `buildPrompt`
 * with `voice.samples` empty and the prompt tells the model to write neutral
 * rather than invent a way of speaking — the documented thin-profile fallback,
 * now the default. The owner was shown that and confirmed both halves the same
 * day: the caption screen stays out, the Instagram field stays in.
 *
 * **So neither is a regression to undo.** Restoring the captions is a product
 * decision, not a repair, and the thing that makes the handle honest is an
 * integration rather than an edit to this file.
 */
export function ProfileOnboardingScreen(): JSX.Element {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const existing = useProfile();
  const save = useSaveProfile();
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [checking, setChecking] = useState(false);
  const [confirmNote, setConfirmNote] = useState('');
  const confirmInstagram = useConfirmInstagram();

  useEffect(() => {
    const profile = existing.data?.profile;
    if (profile) {
      // Editing rather than starting over: she reached this screen from "Meu
      // perfil" as often as from onboarding.
      setDraft(toDraft(profile));
    }
  }, [existing.data]);

  const state = accessStateOf(existing.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  /**
   * Opens the profile so she can look at it, and asks whether it is hers.
   *
   * It called `useInstagramLookup` until 2026-08-08, to fill the follower and
   * post counts. Those fields left the screen the same day, and a lookup whose
   * only result was two numbers nobody can see is a scrape she did not ask
   * for. The route, the kill switch and the hook all stay: only this screen
   * stopped calling them.
   */
  /**
   * What confirming writes, and what it does when the read is refused.
   *
   * The stamp lands either way. `instagramConfirmedAt` records that she looked
   * at the profile and said it was hers, which is true whether or not Instagram
   * answered us — tying it to the read would make a refused lookup delete her
   * own confirmation.
   */
  async function confirmProfile() {
    const handle = instagramHandle(draft.instagram);
    set('instagramConfirmedAt', new Date().toISOString());
    setChecking(false);
    if (!handle) {
      return;
    }

    try {
      const answer = await confirmInstagram.mutateAsync(handle.slice(1));
      if (answer.status !== 'found') {
        setConfirmNote('');
        return;
      }
      set('followerCount', String(answer.profile.followers));
      set('postCount', String(answer.profile.posts));
      set('countsConfirmedAt', answer.profile.fetchedAt);
      if (answer.thumbnailKey) {
        set('instagramThumbnailKey', answer.thumbnailKey);
      }
      setConfirmNote(noteFor(answer.profile.followers, answer.profile.posts, answer.thumbnailKey));
    } catch {
      // Falling back to the manual confirmation is the designed path here, not
      // a failure to report: the lookup is refused often enough that an error
      // beside a field she already filled would be noise.
      setConfirmNote('');
    }
  }

  function openProfile() {
    const handle = instagramHandle(draft.instagram);
    if (!handle) {
      return;
    }
    window.open(`https://instagram.com/${handle.slice(1)}`, '_blank', 'noopener,noreferrer');
    setChecking(true);
  }

  async function finish() {
    const profile = toProfile(draft);
    await save.mutateAsync(profile);
    posthog?.capture('profile_saved', {
      has_offer: Boolean(profile.offer),
      has_constraints: Boolean(profile.constraints),
      platform_count: profile.format.platforms.length,
      preferred_length: profile.format.preferredLength,
    });
    setStep(STEPS);
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 760 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span
          style={{
            font: '600 10px/1 var(--rvk-font-functional)',
            letterSpacing: '.14em',
            textTransform: 'uppercase',
            color: 'var(--rvk-text-tertiary)'
          }}
        >
          Passo {step} de {STEPS}
        </span>
        <div style={{ display: 'flex', gap: 4 }} aria-hidden>
          {Array.from({ length: STEPS }, (_, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 999,
                background:
                  index < step ? 'var(--rvk-accent-default)' : 'var(--rvk-border-subtle)'
              }}
            />
          ))}
        </div>
      </div>

      <Card layer={step === STEPS ? 'expressive' : 'functional'} padding="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {step === 1 && (
            /* Two groups, not eight fields in a row: who she is, then where she
               already publishes. The gap between groups is twice the gap inside
               one, which is what tells them apart without a divider. */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              <Heading>Quem é você, e para quem você fala</Heading>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <ChipChoice
                  legend="Seu nicho"
                  hint="Marque o seu, ou escreva do seu jeito."
                  choices={NICHES}
                  value={draft.niche}
                  onChange={(value) => set('niche', value)}
                  add="escrever o meu nicho"
                />
                <Field
                  className="rvk-hint-above"
                  label="Para quem você fala"
                  required
                  hint="Idade, onde essa pessoa mora e o que ela procura quando te encontra."
                >
                  <Textarea
                    rows={3}
                    value={draft.audience}
                    onChange={(event) => set('audience', event.target.value)}
                  />
                </Field>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Shown, never chosen. She stopped picking a file on 2026-08-19:
                  confirming the profile below reads the picture that is already
                  on it, and two ways to answer one question is how the photo
                  here and the photo on her Instagram start disagreeing.

                  It stays on screen because the read is silent otherwise — the
                  avatar is the only place she can see that the confirmation
                  worked, and `PHOTO_HINT` says where it comes from before it
                  arrives. */}
              <Field
                className="rvk-hint-above"
                label="Foto de perfil"
                hint={PHOTO_HINT}
                access="readonly"
              >
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ProfileAvatar
                    objectKey={draft.instagramThumbnailKey}
                    name={instagramHandle(draft.instagram)?.slice(1) ?? 'Você'}
                    size="md"
                  />
                  <span
                    style={{
                      font: '500 11px/1 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-tertiary)'
                    }}
                  >
                    {draft.instagramThumbnailKey === '' ? 'ainda sem foto' : 'do seu Instagram'}
                  </span>
                </div>
              </Field>
              <Field
                className="rvk-hint-above"
                label="Perfil no Instagram"
                required
                hint={
                  draft.instagram.trim() !== '' && !isInstagramHandle(draft.instagram)
                    ? 'Isso não parece um perfil do Instagram. Confira o endereço.'
                    : 'Fica guardado no seu perfil.'
                }
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* The design-system Input fills its line, so it needs a growing
                      box of its own or the button wraps under it. */}
                  <div style={{ flex: '1 1 220px' }}>
                    <Input
                      placeholder="instagram.com/rayrovisk"
                      value={draft.instagram}
                      onChange={(event) => {
                        set('instagram', event.target.value);
                        set('instagramConfirmedAt', '');
                        setChecking(false);
                        setConfirmNote('');
                      }}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    onClick={openProfile}
                    access={isInstagramHandle(draft.instagram) ? undefined : 'unavailable'}
                    accessReason="Escreva um perfil válido primeiro."
                  >
                    Conferir
                  </Button>
                  {draft.instagramConfirmedAt !== '' && (
                    <span
                      style={{
                        font: '500 11px/1 var(--rvk-font-functional)',
                        color: 'var(--rvk-text-tertiary)'
                      }}
                    >
                      conferido
                    </span>
                  )}
                </div>
              </Field>
              {checking && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ font: '400 12px/1.4 var(--rvk-font-functional)' }}>
                    {/* "Abrimos", não "Encontramos": desde que o lookup saiu,
                        a tela abriu uma aba e não procurou nada. */}
                    Abrimos {instagramHandle(draft.instagram)}. É o seu perfil?
                  </span>
                  <Button
                    variant="secondary"
                    loading={confirmInstagram.isPending}
                    onClick={() => void confirmProfile()}
                  >
                    Sim, é esse
                  </Button>
                  <Button variant="ghost" onClick={() => setChecking(false)}>
                    Não, corrigir
                  </Button>
                </div>
              )}
              {confirmNote !== '' && (
                <p
                  style={{
                    margin: 0,
                    font: '400 13px/1.5 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)'
                  }}
                >
                  {confirmNote}
                </p>
              )}
              </div>
            </div>
          )}

          {step === 2 && (
            <>
              <Heading>O que você vende?</Heading>
              <Field className="rvk-hint-above" label="Produto ou serviço" required>
                <Input value={draft.sells} onChange={(event) => set('sells', event.target.value)} />
              </Field>
              <ChipChoice
                legend="Faixa de preço"
                hint="Isso altera o tom do roteiro."
                choices={PRICE_RANGES}
                value={draft.priceRange}
                onChange={(value) => set('priceRange', value)}
              />
              <ChipChoice
                legend="Objeção mais comum"
                hint="O que ela pensa antes de desistir da compra."
                choices={OBJECTIONS}
                value={draft.objection}
                onChange={(value) => set('objection', value)}
                add="escrever a minha"
              />
            </>
          )}

          {step === 3 && (
            <>
              <Heading>Como você produz o seu conteúdo</Heading>
              {/* `RadioGroup`'s own `label` is an `aria-label` and draws
                  nothing, so the question has to be written out. The two
                  strings differ on purpose: a screen reader reads both. */}
              <span style={{ font: '500 13.5px/1.4 var(--rvk-font-functional)' }}>
                Com que frequência você aparece falando no vídeo?
              </span>
              <RadioGroup
                label="Você aparece em vídeo?"
                value={draft.appearsOnCamera}
                onValueChange={(value) => set('appearsOnCamera', value)}
              >
                {/* No description on this one: "Sim, sempre" answers the
                    question above it whole, and a line explaining it would be
                    the same sentence twice. */}
                <Radio value="sempre" label="Sim, sempre" />
                <Radio
                  value="as-vezes"
                  label="Às vezes"
                  description="O prompt oferece versão com e sem a sua fala."
                />
                <Radio
                  value="nao"
                  label="Não apareço"
                  description="Roteiros só de imagem, texto e voz em off."
                />
              </RadioGroup>
              <ChipChoice
                legend="Duração do conteúdo"
                choices={LENGTHS}
                value={draft.preferredLength}
                onChange={(value) => set('preferredLength', value)}
                clearable={false}
              />
              {/* No field to write her own: `format.platforms` is a closed set,
                  and a shape nobody wrote a prompt for lands in the format
                  block as a word the generator cannot act on. */}
              <ChipMulti
                legend="Formatos que você mais usa"
                choices={FORMATS}
                values={draft.platforms}
                onChange={(values) => set('platforms', values)}
              />
            </>
          )}

          {step === 4 && (
            <VoiceRulesStep
              rules={draft}
              onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
            />
          )}

          {step === STEPS && (
            <>
              <Heading>Pronto. O seu perfil está salvo.</Heading>
              <p
                style={{
                  margin: 0,
                  font: '400 14.5px/1.6 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-secondary)'
                }}
              >
                Você não precisa explicar o seu nicho de novo. A partir de agora, é só descrever a
                tarefa.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Completeness label="Identidade" done={draft.niche !== '' && draft.audience !== ''} />
                {/* The `Voz · N de 5 trechos` row is gone with the screen that
                    filled it. Counting samples nobody can enter would have left
                    every profile reading "0 de 5" forever, which is a true
                    number describing a field she was never shown. */}
                <Completeness label="Formato" done={draft.platforms.length > 0} />
                <Completeness label="Oferta" done={draft.sells !== ''} />
                {/* Every card of step 4 counts. Reading only the two forbidden
                    lists said "dá para melhorar" to someone who had filled the
                    step end to end with words. */}
                <Completeness
                  label="Restrições"
                  done={
                    draft.forbiddenClaims.length > 0 ||
                    draft.forbiddenTopics.length > 0 ||
                    draft.wordsUsed.length > 0 ||
                    draft.wordsAvoided.length > 0
                  }
                />
              </div>
            </>
          )}

          {save.isError && (
            <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)', color: 'var(--rvk-color-error, #b3261e)' }}>
              {(save.error as Error).message}
            </p>
          )}
        </div>
      </Card>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap'
        }}
      >
        <Button
          variant="ghost"
          iconStart={<ChevronLeftIcon size={14} />}
          onClick={() => setStep((current) => Math.max(1, current - 1))}
          access={step === 1 ? 'unavailable' : undefined}
          accessReason="Você está no primeiro passo."
        >
          Voltar
        </Button>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {step === 4 && (
            <Button variant="ghost" onClick={() => void finish()}>
              Pular esta etapa
            </Button>
          )}
          {step < 4 && (
            <Button
              variant="primary"
              iconEnd={<ChevronRightIcon size={14} />}
              onClick={() => setStep((current) => current + 1)}
              access={missingOn(step, draft) ? 'unavailable' : undefined}
              accessReason={missingOn(step, draft) ?? ''}
            >
              Continuar
            </Button>
          )}
          {/* `Continuar` rather than `Salvar perfil`, per the design. It is the
              same action — this is the last screen she fills, so it saves and
              moves to the summary; only the word changed. */}
          {step === 4 && (
            <Button
              variant="primary"
              loading={save.isPending}
              iconEnd={<ChevronRightIcon size={14} />}
              onClick={() => void finish()}
            >
              Continuar
            </Button>
          )}
          {step === STEPS && (
            <Button
              variant="primary"
              iconEnd={<ChevronRightIcon size={14} />}
              onClick={() => navigate('/gerador/montar')}
            >
              Ir para o gerador
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function Heading({ children }: { children: string }): JSX.Element {
  return <h1 style={{ margin: 0, font: '400 24px/1.2 var(--rvk-font-expressive)' }}>{children}</h1>;
}

function Completeness({ label, done }: { label: string; done: boolean }): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap'
      }}
    >
      <span style={{ font: '400 13.5px/1.5 var(--rvk-font-functional)' }}>{label}</span>
      <Badge tone={done ? 'success' : 'neutral'} variant="subtle" size="sm">
        {done ? 'completo' : 'dá para melhorar'}
      </Badge>
    </div>
  );
}

const counted = new Intl.NumberFormat('pt-BR');

/**
 * What the confirmation actually got, named. Without this she sees a photo
 * change and two numbers she cannot see land somewhere else, and has no way to
 * tell a working read from a refused one.
 */
function noteFor(followers: number, posts: number, thumbnailKey: string | null): string {
  const numbers = `${counted.format(followers)} seguidores e ${counted.format(posts)} publicações`;
  return thumbnailKey
    ? `Pegamos a sua foto e os seus números: ${numbers}. Eles já estão em Minhas redes.`
    : `Pegamos os seus números: ${numbers}. Eles já estão em Minhas redes.`;
}

/**
 * Absent, never null. `creatorProfileSchema` marks `offer` and `constraints`
 * `.optional()`, which accepts a missing key and rejects an explicit null — and
 * blank optional fields are the normal path here, since step 2 requires only
 * "produto ou serviço" and step 5 can be skipped outright. Sending null turned
 * finishing onboarding into a 400 that named a field she was never asked to
 * fill.
 */
function toProfile(draft: Draft): ProfileDraft {
  const offer = compact({
    product: draft.sells,
    priceRange: draft.priceRange,
    commonObjection: draft.objection
  });

  const constraints =
    draft.forbiddenTopics.length === 0 && draft.forbiddenClaims.length === 0 && !draft.regulated
      ? undefined
      : {
          forbiddenTopics: draft.forbiddenTopics,
          forbiddenClaims: draft.forbiddenClaims,
          regulatedNiche: draft.regulated
        };

  return {
    identity: {
      niche: draft.niche,
      audience: draft.audience,
      sells: draft.sells,
      // Omitted when blank: `instagram` is `.optional()`, which takes a missing
      // key and rejects an explicit empty-then-null round trip.
      ...(draft.instagram.trim() === '' ? {} : { instagram: draft.instagram.trim() }),
      ...(draft.instagramConfirmedAt === ''
        ? {}
        : { instagramConfirmedAt: draft.instagramConfirmedAt }),
      ...(draft.followerCount === '' ? {} : { followerCount: Number(draft.followerCount) }),
      ...(draft.postCount === '' ? {} : { postCount: Number(draft.postCount) }),
      ...(draft.countsConfirmedAt === '' ? {} : { countsConfirmedAt: draft.countsConfirmedAt }),
      ...(draft.instagramThumbnailKey === ''
        ? {}
        : { instagramThumbnailKey: draft.instagramThumbnailKey })
    },
    voice: {
      // Whatever an older profile arrived with, unchanged. Nothing on screen
      // writes to this any more, so for a new profile it is `[]`.
      samples: draft.samples.map((item) => item.trim()).filter((item) => item !== ''),
      wordsUsed: draft.wordsUsed,
      wordsAvoided: draft.wordsAvoided
    },
    format: {
      // "Às vezes" counts as appearing: the prompt then offers a version with
      // her speaking and one without, which is what the option promises.
      appearsOnCamera: draft.appearsOnCamera !== 'nao',
      preferredLength: draft.preferredLength,
      platforms: draft.platforms
    },
    ...(offer ? { offer } : {}),
    ...(constraints ? { constraints } : {})
  };
}

/**
 * What this step still needs, as the sentence the disabled button explains
 * itself with. `niche`, `audience` and `sells` are `.min(1)` in
 * `creatorProfileSchema`, so without this the refusal arrives as a rejected PUT
 * after she has filled four screens.
 *
 * Step 3 is absent on purpose: every control there carries a default, and an
 * empty `platforms` is valid.
 */
function missingOn(step: number, draft: Draft): string | undefined {
  if (step === 1) {
    if (draft.niche.trim() === '') {
      return 'Escreva o seu nicho para continuar.';
    }
    if (draft.audience.trim() === '') {
      return 'Diga para quem você fala para continuar.';
    }
    return isInstagramHandle(draft.instagram)
      ? undefined
      : 'Preencha o seu perfil no Instagram para continuar.';
  }
  if (step === 2) {
    return draft.sells.trim() === '' ? 'Diga o que você vende para continuar.' : undefined;
  }
  return undefined;
}

/** Drops the blank entries, and the whole block when nothing is left. */
function compact(
  values: Record<string, string>
): Record<string, string> | undefined {
  const filled = Object.entries(values).filter(([, value]) => value.trim() !== '');
  return filled.length === 0 ? undefined : Object.fromEntries(filled);
}

function toDraft(profile: CreatorProfile): Draft {
  return {
    niche: profile.identity.niche,
    audience: profile.identity.audience,
    instagram: profile.identity.instagram ?? '',
    instagramConfirmedAt: profile.identity.instagramConfirmedAt ?? '',
    instagramThumbnailKey: profile.identity.instagramThumbnailKey ?? '',
    followerCount: profile.identity.followerCount?.toString() ?? '',
    postCount: profile.identity.postCount?.toString() ?? '',
    countsConfirmedAt: profile.identity.countsConfirmedAt ?? '',
    sells: profile.identity.sells,
    priceRange: profile.offer?.priceRange ?? '',
    objection: profile.offer?.commonObjection ?? '',
    // Carried through untouched. She can no longer edit these, and dropping
    // them here would erase captions an older profile still uses the next time
    // she edits anything else.
    samples: [...profile.voice.samples],
    wordsUsed: [...profile.voice.wordsUsed],
    wordsAvoided: [...profile.voice.wordsAvoided],
    appearsOnCamera: profile.format.appearsOnCamera ? 'as-vezes' : 'nao',
    preferredLength: profile.format.preferredLength,
    platforms: profile.format.platforms,
    forbiddenTopics: [...(profile.constraints?.forbiddenTopics ?? [])],
    forbiddenClaims: [...(profile.constraints?.forbiddenClaims ?? [])],
    regulated: profile.constraints?.regulatedNiche ?? false
  };
}
